import { yupResolver } from '@hookform/resolvers/yup';
import {
  ChevronDown,
  ChevronUp,
  Grid3X3,
  List,
  Search,
  SlidersHorizontal,
  Upload,
} from 'lucide-react';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PhotoFilterFormData, photoFilterSchema } from '../../schemas/photoSchemas';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchPhotos,
  fetchPhotoStats,
  resetFilters,
  setDateRange,
  setSearchTerm,
  setSelectedCategory,
  setSelectedLocation,
  setSelectedProject,
  setSortBy,
  setSortOrder,
  setUploadedBy,
  setViewMode,
} from '../../store/slices/photosSlice';
import type { PhotoCategory } from '../../types';
import { Dropdown, Input, DatePicker } from '../forms';

interface PhotoFiltersHookFormProps {
  isProjectSpecific: boolean;
  projectId?: string;
  totalPhotos: number;
  selectedPhotosCount: number;
  onUploadClick: () => void;
}

export const PhotoFiltersHookForm: React.FC<PhotoFiltersHookFormProps> = memo(
  ({ isProjectSpecific, projectId, totalPhotos, selectedPhotosCount, onUploadClick }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [showAdvanced, setShowAdvanced] = useState(false);

    const {
      query,
      categories,
      projects,
      sortOptions,
      permissions,
      viewMode,
      filterLoading,
      loading,
    } = useAppSelector(state => state.photos);

    const { control, watch, reset } = useForm<PhotoFilterFormData>({
      resolver: yupResolver(photoFilterSchema) as any,
      defaultValues: {
        search: query.search,
        category: query.category as PhotoCategory | '',
        project_id: query.project_id,
        location: query.location,
        uploaded_by: query.uploaded_by,
        sort_by: query.sort_by,
        sort_order: query.sort_order,
        date_from: query.date_from,
        date_to: query.date_to,
      },
      mode: 'onChange',
    });

    // Debounced query updates
    useEffect(() => {
      const subscription = watch(values => {
        const timeoutId = setTimeout(() => {
          let hasChanges = false;

          if (
            values.search !== query.search ||
            values.category !== query.category ||
            values.project_id !== query.project_id ||
            values.location !== query.location ||
            values.uploaded_by !== query.uploaded_by ||
            values.sort_by !== query.sort_by ||
            values.sort_order !== query.sort_order ||
            values.date_from !== query.date_from ||
            values.date_to !== query.date_to
          ) {
            hasChanges = true;
          }

          if (hasChanges) {
            const newQuery = {
              search: values.search || '',
              category: values.category as PhotoCategory | undefined,
              project_id: values.project_id || (isProjectSpecific ? projectId : ''),
              location: values.location || '',
              uploaded_by: values.uploaded_by || '',
              sort_by: (values.sort_by as any) || 'uploaded_at',
              sort_order: (values.sort_order as 'asc' | 'desc') || 'desc',
              date_from: values.date_from || '',
              date_to: values.date_to || '',
              page: 1,
              limit: 20,
            };

            dispatch(setSearchTerm(newQuery.search));
            dispatch(setSelectedCategory(newQuery.category || undefined));
            dispatch(setSelectedProject(newQuery.project_id || ''));
            dispatch(setSelectedLocation(newQuery.location));
            dispatch(setUploadedBy(newQuery.uploaded_by));
            dispatch(setSortBy(newQuery.sort_by as any));
            dispatch(setSortOrder(newQuery.sort_order));
            dispatch(setDateRange({ from: newQuery.date_from, to: newQuery.date_to }));

            dispatch(fetchPhotos({ query: newQuery, projectId }));
            if (
              values.search ||
              values.category ||
              values.location ||
              values.date_from ||
              values.date_to
            ) {
              dispatch(fetchPhotoStats(projectId));
            }
          }
        }, 400);

        return () => clearTimeout(timeoutId);
      });

      return () => subscription.unsubscribe();
    }, [watch, dispatch, query, projectId, isProjectSpecific]);

    useEffect(() => {
      reset({
        search: query.search,
        category: query.category as PhotoCategory | '',
        project_id: query.project_id,
        location: query.location,
        uploaded_by: query.uploaded_by,
        sort_by: query.sort_by,
        sort_order: query.sort_order,
        date_from: query.date_from,
        date_to: query.date_to,
      });
    }, [reset, query]);

    const handleResetFilters = useCallback(() => {
      dispatch(resetFilters());
      reset({
        search: '',
        category: '',
        project_id: isProjectSpecific ? projectId : '',
        location: '',
        uploaded_by: '',
        sort_by: 'uploaded_at',
        sort_order: 'desc',
        date_from: '',
        date_to: '',
      });
    }, [dispatch, reset, isProjectSpecific, projectId]);

    const handleViewModeChange = useCallback(
      (mode: 'gallery' | 'list') => {
        dispatch(setViewMode(mode));
      },
      [dispatch]
    );

    const isLoading = loading || filterLoading;

    return (
      <div className="bg-white border-b border-gray-100">
        {/* Main Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <Controller
              name="search"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('photos.search.placeholder')}
                    value={field.value}
                    onChange={field.onChange}
                    className="pl-9 h-9 text-sm"
                    disabled={isLoading}
                  />
                </div>
              )}
            />
          </div>

          {/* Category Filter */}
          <div className="w-40">
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Dropdown
                  placeholder={t('photos.filters.allCategories')}
                  options={[
                    { value: '', label: t('photos.filters.allCategories') },
                    ...categories.map(c => ({ value: c.value, label: c.label })),
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                  className="h-9 text-sm"
                />
              )}
            />
          </div>

          {/* Sort */}
          <div className="w-36">
            <Controller
              name="sort_by"
              control={control}
              render={({ field }) => (
                <Dropdown
                  placeholder={t('photos.sorting.sortBy')}
                  options={
                    sortOptions.length > 0
                      ? sortOptions
                      : [
                          { value: 'uploaded_at', label: t('photos.sorting.uploadDate') },
                          { value: 'title', label: t('photos.sorting.title') },
                          { value: 'category', label: t('photos.sorting.category') },
                        ]
                  }
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                  className="h-9 text-sm"
                />
              )}
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => handleViewModeChange('gallery')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'gallery'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={t('photos.views.gallery')}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('list')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={t('photos.views.list')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Advanced Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
              showAdvanced ? 'bg-brand-50 text-brand-600' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {/* Upload Button */}
          {permissions.upload && (
            <button
              type="button"
              onClick={onUploadClick}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              <Upload className="h-4 w-4" />
              {t('photos.upload.uploadPhotos')}
            </button>
          )}

          {/* Results Count */}
          <div className="text-xs text-gray-500 ml-auto">
            {totalPhotos} {t('photos.stats.results')}
            {selectedPhotosCount > 0 && (
              <span className="ml-2 text-brand-600 font-medium">
                ({selectedPhotosCount} {t('photos.stats.selected')})
              </span>
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="border-t border-gray-100 p-3">
            <div className="flex flex-wrap items-end gap-3">
              {/* Project Filter (Global only) */}
              {!isProjectSpecific && (
                <div className="w-48">
                  <label className="block text-xs text-gray-500 mb-1">Project</label>
                  <Controller
                    name="project_id"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        placeholder={t('photos.filters.allProjects')}
                        options={[
                          { value: '', label: t('photos.filters.allProjects') },
                          ...projects,
                        ]}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                        className="h-9 text-sm"
                      />
                    )}
                  />
                </div>
              )}

              {/* Location */}
              <div className="w-40">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('photos.filters.location')}
                </label>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder={t('photos.filters.location')}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                      className="h-9 text-sm"
                    />
                  )}
                />
              </div>

              {/* Date From */}
              <div className="w-36">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('photos.filters.dateFrom')}
                </label>
                <Controller
                  name="date_from"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={date => field.onChange(date || '')}
                      disabled={isLoading}
                      className="h-9 text-sm"
                    />
                  )}
                />
              </div>

              {/* Date To */}
              <div className="w-36">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('photos.filters.dateTo')}
                </label>
                <Controller
                  name="date_to"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={date => field.onChange(date || '')}
                      disabled={isLoading}
                      className="h-9 text-sm"
                    />
                  )}
                />
              </div>

              {/* Sort Order */}
              <div className="w-28">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('photos.filters.sortOrder')}
                </label>
                <Controller
                  name="sort_order"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      options={[
                        { value: 'desc', label: t('photos.filters.descending') },
                        { value: 'asc', label: t('photos.filters.ascending') },
                      ]}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                      className="h-9 text-sm"
                    />
                  )}
                />
              </div>

              {/* Reset Button */}
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
              >
                {t('photos.filters.resetFilters')}
              </button>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {filterLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-600 border-t-transparent"></div>
          </div>
        )}
      </div>
    );
  }
);

PhotoFiltersHookForm.displayName = 'PhotoFiltersHookForm';
